import { TOGGLE_LINK_COMMAND } from '@lexical/link';
import { mergeRegister } from '@lexical/utils';
import { ProLightBold } from '@nexelem/icons/ProLightBold';
import { ProLightItalic } from '@nexelem/icons/ProLightItalic';
import { ProLightLinkHorizontal } from '@nexelem/icons/ProLightLinkHorizontal';
import { ProLightStrikethrough } from '@nexelem/icons/ProLightStrikethrough';
import { ProLightSubscript } from '@nexelem/icons/ProLightSubscript';
import { ProLightSuperscript } from '@nexelem/icons/ProLightSuperscript';
import { ProLightUnderline } from '@nexelem/icons/ProLightUnderline';
import { getTranslation } from '@nexelem/utils';
import {
  $getSelection,
  COMMAND_PRIORITY_LOW,
  FORMAT_TEXT_COMMAND,
  LexicalEditor,
  SELECTION_CHANGE_COMMAND,
} from 'lexical';
import { JSX, useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { getDOMRangeRect } from '../../utils/getDOMRangeRect';
import { setFloatingElemPosition } from '../../utils/setFloatingElemPosition';
import { StyledContainer, StyledIconButton } from './index.styled';

type TextFormatFloatingToolbarProps = {
  editor: LexicalEditor;
  anchorElem: HTMLElement;
  isBold: boolean;
  isItalic: boolean;
  isLink: boolean;
  isStrikethrough: boolean;
  isSubscript: boolean;
  isSuperscript: boolean;
  isUnderline: boolean;
};

export function TextFormatFloatingToolbar({
  editor,
  anchorElem,
  isLink,
  isBold,
  isItalic,
  isUnderline,
  isStrikethrough,
  isSubscript,
  isSuperscript,
}: TextFormatFloatingToolbarProps): JSX.Element {
  const popupCharStylesEditorRef = useRef<HTMLDivElement | null>(null);

  const { t: translation } = useTranslation();
  const t = useMemo(() => getTranslation(translation, 'components.richTextEditor.floatingToolbar'), [translation]);

  const insertLink = useCallback(() => {
    if (!isLink) {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, 'https://');
    } else {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
    }
  }, [editor, isLink]);

  function mouseMoveListener(e: MouseEvent) {
    if (popupCharStylesEditorRef?.current && (e.buttons === 1 || e.buttons === 3)) {
      if (popupCharStylesEditorRef.current.style.pointerEvents !== 'none') {
        const x = e.clientX;
        const y = e.clientY;
        const elementUnderMouse = document.elementFromPoint(x, y);

        if (!popupCharStylesEditorRef.current.contains(elementUnderMouse)) {
          // Mouse is not over the target element => not a normal click, but probably a drag
          popupCharStylesEditorRef.current.style.pointerEvents = 'none';
        }
      }
    }
  }

  function mouseUpListener(e: MouseEvent) {
    if (popupCharStylesEditorRef?.current) {
      if (popupCharStylesEditorRef.current.style.pointerEvents !== 'auto') {
        popupCharStylesEditorRef.current.style.pointerEvents = 'auto';
      }
    }
  }

  useEffect(() => {
    if (popupCharStylesEditorRef?.current) {
      document.addEventListener('mousemove', mouseMoveListener);
      document.addEventListener('mouseup', mouseUpListener);

      return () => {
        document.removeEventListener('mousemove', mouseMoveListener);
        document.removeEventListener('mouseup', mouseUpListener);
      };
    }
    return undefined;
  }, [popupCharStylesEditorRef]);

  const updateTextFormatFloatingToolbar = useCallback(() => {
    const selection = $getSelection();

    const popupCharStylesEditorElem = popupCharStylesEditorRef.current;
    const nativeSelection = window.getSelection();

    if (popupCharStylesEditorElem === null) {
      return;
    }

    const rootElement = editor.getRootElement();
    if (
      selection !== null &&
      nativeSelection !== null &&
      !nativeSelection.isCollapsed &&
      rootElement !== null &&
      rootElement.contains(nativeSelection.anchorNode)
    ) {
      const rangeRect = getDOMRangeRect(nativeSelection, rootElement);

      setFloatingElemPosition(rangeRect, popupCharStylesEditorElem, anchorElem);
    }
  }, [editor, anchorElem]);

  useEffect(() => {
    const scrollerElem = anchorElem.parentElement;

    const update = () => {
      editor.getEditorState().read(() => {
        updateTextFormatFloatingToolbar();
      });
    };

    window.addEventListener('resize', update);
    if (scrollerElem) {
      scrollerElem.addEventListener('scroll', update);
    }

    return () => {
      window.removeEventListener('resize', update);
      if (scrollerElem) {
        scrollerElem.removeEventListener('scroll', update);
      }
    };
  }, [editor, updateTextFormatFloatingToolbar, anchorElem]);

  useEffect(() => {
    editor.getEditorState().read(() => {
      updateTextFormatFloatingToolbar();
    });
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateTextFormatFloatingToolbar();
        });
      }),

      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          updateTextFormatFloatingToolbar();
          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
    );
  }, [editor, updateTextFormatFloatingToolbar]);

  const onClickBold = useCallback(() => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold');
  }, [editor]);

  const onClickItalic = useCallback(() => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic');
  }, [editor]);

  const onClickUnderline = useCallback(() => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline');
  }, [editor]);

  const onClickStrikethrough = useCallback(() => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough');
  }, [editor]);

  const onClickSubscript = useCallback(() => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'subscript');
  }, [editor]);

  const onClickSuperscript = useCallback(() => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'superscript');
  }, [editor]);

  return (
    <StyledContainer ref={popupCharStylesEditorRef}>
      {editor.isEditable() && (
        <>
          <StyledIconButton
            aria-label={t('bold.ariaLabel')}
            Icon={ProLightBold}
            isActive={isBold}
            onClick={onClickBold}
          />
          <StyledIconButton
            aria-label={t('italic.ariaLabel')}
            Icon={ProLightItalic}
            isActive={isItalic}
            onClick={onClickItalic}
          />
          <StyledIconButton
            aria-label={t('underline.ariaLabel')}
            Icon={ProLightUnderline}
            isActive={isUnderline}
            onClick={onClickUnderline}
          />
          <StyledIconButton
            aria-label={t('strikethrough.ariaLabel')}
            Icon={ProLightStrikethrough}
            isActive={isStrikethrough}
            onClick={onClickStrikethrough}
          />
          <StyledIconButton
            aria-label={t('subscript.ariaLabel')}
            disabled={isSuperscript}
            Icon={ProLightSubscript}
            isActive={isSubscript}
            title={t('subscript.title')}
            onClick={onClickSubscript}
          />
          <StyledIconButton
            aria-label={t('superscript.ariaLabel')}
            disabled={isSubscript}
            Icon={ProLightSuperscript}
            isActive={isSuperscript}
            title={t('superscript.title')}
            onClick={onClickSuperscript}
          />
          <StyledIconButton aria-label={t('insertLink.ariaLabel')} Icon={ProLightLinkHorizontal} onClick={insertLink} />
        </>
      )}
    </StyledContainer>
  );
}
